import type {ReaderPosition,WorkspaceContentItem,WorkspaceChatItem,WorkspaceSnapshot} from '@reados/contracts';
export type {WorkspaceSnapshot} from '@reados/contracts';
/** `active` is focus; displayed slots are ordered by the tab strip, as on iPad. */
export interface TabState {ids:string[];active:string|null;secondary:string|null;split:boolean}
export const emptyTabs:TabState={ids:[],active:null,secondary:null,split:false};
export function normalizeTabs(value:unknown):TabState{
 const input=value&&typeof value==='object'?value as Partial<TabState>:{};
 const ids=Array.isArray(input.ids)?[...new Set(input.ids.filter((id):id is string=>typeof id==='string'&&id.length>0))].slice(0,100):[];
 const active=input.active&&ids.includes(input.active)?input.active:ids[0]||null;
 const secondary=input.split&&input.secondary&&ids.includes(input.secondary)&&input.secondary!==active?input.secondary:null;
 return {ids,active,secondary,split:input.split===true};
}
export function openTab(state:TabState,id:string):TabState{
 const ids=state.ids.includes(id)?state.ids:[...state.ids,id];
 if(!state.split)return {...state,ids,active:id,secondary:null};
 if(id===state.active)return state.ids===ids?state:{...state,ids};
 if(id===state.secondary)return {...state,ids,active:id,secondary:state.active};
 return {...state,ids,active:id,secondary:state.active};
}
export function toggleTab(state:TabState,id:string):TabState{
 if(state.split&&state.secondary){
  if(id===state.active)return {...state,active:state.secondary,secondary:null};
  if(id===state.secondary)return {...state,secondary:null};
 }
 return openTab(state,id);
}
export function focusTab(state:TabState,id:string):TabState{
 if(id===state.active||!visibleTabIds(state,true).includes(id))return state;
 return {...state,active:id,secondary:state.active};
}
export function closeTab(state:TabState,id:string):TabState{
 const ids=state.ids.filter(value=>value!==id);
 const active=state.active===id?(state.secondary||ids[Math.min(state.ids.indexOf(id),ids.length-1)]||null):state.active;
 const secondary=state.secondary===id||state.secondary===active?null:state.secondary;
 return {...state,ids,active,secondary};
}
export function toggleSplit(state:TabState):TabState{return {...state,split:!state.split,secondary:null};}
export function visibleTabIds(state:TabState,paneVisible:boolean):string[]{
 if(!paneVisible)return [];
 const visible=[state.active,state.split?state.secondary:null];
 return state.ids.filter(id=>visible.includes(id));
}
/** Materialize a sending draft in its existing slot without promoting hidden tabs. */
export function persistedChatTabs(state:TabState,threadId:string,pendingId?:string):TabState{
 const replace=(id:string|null)=>id===pendingId?threadId:id;
 const ids=[...new Set(state.ids.map(id=>replace(id)!).filter(id=>!id.startsWith('pending')))];
 const active=replace(state.active),secondary=replace(state.secondary);
 return {ids:ids.includes(threadId)?ids:[...ids,threadId],active:active&&ids.includes(active)?active:null,secondary:secondary&&ids.includes(secondary)?secondary:null,split:state.split};
}
export interface ReaderTab {id:string;fileId?:string;artifactPath?:string;title:string;kind:string}
export interface ReaderSnapshot extends ReaderPosition {fileId?:string}
export function buildWorkspaceContext(input:{readers:ReaderTab[];readerTabs:TabState;chatTabs:TabState;readerVisible:boolean;chatVisible:boolean;positions:Record<string,ReaderSnapshot>;currentThreadId:string;focusedArea:'reader'|'chat'}):WorkspaceSnapshot{
 const visibleReaders=visibleTabIds(input.readerTabs,input.readerVisible),visibleChats=visibleTabIds(input.chatTabs,input.chatVisible);
 const content:WorkspaceSnapshot['content']={focused:null,visible:[],open_but_not_visible:[]};
 for(const tab of input.readers){const {fileId:_,...position}=input.positions[tab.id]||{};const hasPosition=Object.keys(position).length>0;const isVisible=visibleReaders.includes(tab.id);const item:WorkspaceContentItem={tabId:tab.id,...(tab.fileId?{fileId:tab.fileId}:{}),...(tab.artifactPath?{artifactPath:tab.artifactPath}:{}),kind:tab.kind,...(hasPosition?(isVisible?{position}:{lastKnownPosition:position}):{})};if(isVisible&&tab.id===input.readerTabs.active&&input.focusedArea==='reader')content.focused=item;else if(isVisible)content.visible.push(item);else content.open_but_not_visible.push(item);}
 const current={tabId:input.currentThreadId,threadId:input.currentThreadId};const chat:WorkspaceSnapshot['chat']={current,focused:input.focusedArea==='chat'&&visibleChats.includes(input.currentThreadId)?current:null,visible:[],open_but_not_visible:[]};
 for(const id of input.chatTabs.ids){const item={tabId:id,threadId:id};if(visibleChats.includes(id)){if(id!==chat.focused?.threadId)chat.visible.push(item);}else chat.open_but_not_visible.push(item);}
 return {version:1,content,chat};
}
